import { useRef } from 'react';
import { Alert, Form, Input } from 'antd';
import { AsyncSelect } from 'components/Select';
import FormHelperText from 'components/ui/FormHelperText/FormHelperText';
import UploadFile from 'components/UploadFile/UploadFile';
import { FileList, GetFileContentsEntityTypes } from 'components/FileList/FileList';
import { PROVIDER_FILE_TYPES } from 'services/providers.service';

export default function TaxProfileForm({ careFacilities, orgId, w9FileId, w9FileName, onW9Staged }) {
  const stagedW9Ref = useRef(null);

  return (
    <>
      {!!careFacilities?.length && (
        <Alert
          type="warning"
          className="mb-4"
          message={`Changes will affect ${careFacilities.length} location${careFacilities.length !== 1 ? 's' : ''}:`}
          description={
            <ul className="list-disc list-inside mt-1">
              {careFacilities.map((f) => (
                <li key={f.id}>{f.displayName || f.name || `Location ${f.id}`}</li>
              ))}
            </ul>
          }
          showIcon
        />
      )}
      <Form.Item
        name="businessLegalName"
        label="Legal business name"
        rules={[{ required: true, message: 'Legal business name is required' }]}
      >
        <Input data-testid="businessLegalName" />
      </Form.Item>

      <Form.Item
        name="taxIdNumber"
        label="Tax ID"
        rules={[
          { required: true, message: 'Tax ID is required' },
          { pattern: /^\d{9}$/, message: 'Tax ID must be exactly 9 digits' },
        ]}
        className="mb-0"
      >
        <Input.Password placeholder="Enter 9-digit tax ID" maxLength={9} data-testid="taxIdNumber" />
      </Form.Item>

      <FormHelperText
        text="SSN for individuals and sole proprietors. EIN for business entities (LLC, corporation, partnership). The number must match W-9. (Based on IRS Form W-9, Part I.)"
        className="mb-6"
      />

      <div data-testid="taxClassification-select">
        <Form.Item
          name="taxClassification"
          label="Tax classification"
          rules={[{ required: true, message: 'Tax classification is required' }]}
        >
          <AsyncSelect
            id="taxClassification"
            optionsApiUrl="/options/21"
            placeholder="Select..."
            isClearable
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999999999 }) }}
          />
        </Form.Item>
      </div>

      <Form.Item
        name="w9"
        label="Upload your W-9"
        className="mb-0"
        required
        rules={[
          {
            validator() {
              if (w9FileId || stagedW9Ref.current) return Promise.resolve();
              return Promise.reject(new Error('W-9 is required'));
            },
          },
        ]}
      >
        <>
          {w9FileId && (
            <div className="mb-3">
              <p className="text-sm text-gray-500 mb-1">Current file:</p>
              <FileList
                entityType={GetFileContentsEntityTypes.PROVIDER_ORGANIZATION}
                organizationId={orgId}
                files={[{ id: w9FileId, name: w9FileName ?? 'W-9' }]}
              />
              {onW9Staged && <p className="text-xs text-gray-400 mt-1">Upload a new file below to replace it</p>}
            </div>
          )}
          <UploadFile
            fileCode={PROVIDER_FILE_TYPES.W9.code}
            stagingMode
            onStageFile={(file) => {
              stagedW9Ref.current = file;
              onW9Staged?.(file);
            }}
            uploadButtonText={w9FileId ? 'Upload replacement' : 'Upload file'}
          />
        </>
      </Form.Item>
    </>
  );
}
